/**
 * Pure helpers for the Calendar AUI component: data parsing/normalization,
 * day-range math, timeline layout (with overlap-column resolution) and
 * status → Tailwind-token mapping. No React, no DOM — unit-testable in
 * isolation and mirrors the tolerant parsing approach of `AuiTable.helpers`.
 */

import type {
  CalendarEvent,
  CalendarOwner,
  CalendarDensity,
  CalendarView,
  NormalizedEvent,
  TimezoneSpec,
} from './calendar.types';

// ─── Date utilities (local time) ────────────────────────────────────────────

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Monday-of-week (the references use a Mon-first week / work week). */
export function startOfWeek(d: Date): Date {
  const day = d.getDay(); // 0=Sun…6=Sat
  const diff = (day + 6) % 7; // days since Monday
  return addDays(startOfDay(d), -diff);
}

/** `yyyy-mm-dd` in local time (stable key + the anchor we persist). */
export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse a `yyyy-mm-dd` (or any Date-parseable) string to a local day start. */
export function fromISODate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : startOfDay(d);
}

// ─── Parsing / normalization ────────────────────────────────────────────────

/** Parse an event timestamp. A bare `yyyy-mm-dd` (date-only — typical of
 *  all-day events) is read as LOCAL midnight, not UTC midnight: `new
 *  Date('2026-06-15')` would resolve to the previous calendar day in any
 *  negative-UTC-offset zone (e.g. Jun 14 17:00 in Pacific), dropping the
 *  event off its intended day. Full datetimes (with a `T`/offset) parse as-is. */
function parseEventDate(s: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(s);
}

function normalizeEvent(raw: unknown, idx: number): NormalizedEvent | null {
  if (!raw || typeof raw !== 'object') return null;
  const e = raw as Record<string, unknown>;
  const start = typeof e.start === 'string' ? e.start : undefined;
  if (!start) return null;
  const startDate = parseEventDate(start);
  if (Number.isNaN(startDate.getTime())) return null;

  let endDate: Date;
  if (typeof e.end === 'string') {
    const parsed = parseEventDate(e.end);
    endDate = Number.isNaN(parsed.getTime())
      ? new Date(startDate.getTime() + 3_600_000)
      : parsed;
  } else {
    endDate = new Date(startDate.getTime() + 3_600_000);
  }
  // Guard against inverted ranges.
  if (endDate.getTime() < startDate.getTime()) {
    endDate = new Date(startDate.getTime() + 3_600_000);
  }

  const owners = Array.isArray(e.owners)
    ? (e.owners as unknown[])
        .map((o) => {
          if (typeof o === 'string') return { name: o };
          if (o && typeof o === 'object' && typeof (o as { name?: unknown }).name === 'string') {
            return {
              name: (o as { name: string }).name,
              avatarUrl: (o as { avatarUrl?: string }).avatarUrl,
              email: typeof (o as { email?: unknown }).email === 'string'
                ? (o as { email: string }).email
                : undefined,
            };
          }
          return null;
        })
        .filter((o): o is CalendarOwner => o !== null)
    : undefined;

  return {
    id: typeof e.id === 'string' ? e.id : `evt-${idx}`,
    title: typeof e.title === 'string' ? e.title : 'Untitled',
    start,
    end: typeof e.end === 'string' ? e.end : undefined,
    allDay: e.allDay === true,
    location: typeof e.location === 'string' ? e.location : undefined,
    status: typeof e.status === 'string' ? e.status : undefined,
    owners,
    description: typeof e.description === 'string' ? e.description : undefined,
    conferenceUrl: typeof e.conferenceUrl === 'string' ? e.conferenceUrl : undefined,
    unread: e.unread === true,
    color: typeof e.color === 'string' ? e.color : undefined,
    startDate,
    endDate,
  };
}

/**
 * Accepts either inline `CalendarEvent[]`, a JSON string of the same, or a
 * `{ events: [...] }` wrapper. Returns normalized events sorted by start, plus
 * a human-readable parse error (null on success).
 */
export function parseCalendarData(
  input: string | CalendarEvent[] | undefined,
): { events: NormalizedEvent[]; error: string | null } {
  let rawArray: unknown[];
  try {
    let value: unknown = input;
    if (typeof input === 'string') value = JSON.parse(input);
    if (Array.isArray(value)) {
      rawArray = value;
    } else if (value && typeof value === 'object' && Array.isArray((value as { events?: unknown }).events)) {
      rawArray = (value as { events: unknown[] }).events;
    } else if (value == null) {
      rawArray = [];
    } else {
      return { events: [], error: 'Calendar data must be an array of events or { events: [...] }' };
    }
  } catch (err) {
    return { events: [], error: `Invalid calendar JSON: ${(err as Error).message}` };
  }

  const events = rawArray
    .map((r, i) => normalizeEvent(r, i))
    .filter((e): e is NormalizedEvent => e !== null)
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

  return { events, error: null };
}

// ─── Day ranges ─────────────────────────────────────────────────────────────

export interface DataDays {
  /** Distinct local days that contain at least one event, ascending. */
  days: Date[];
  minDate: Date | null;
  maxDate: Date | null;
}

export function getDataDays(events: NormalizedEvent[]): DataDays {
  const seen = new Map<string, Date>();
  for (const e of events) {
    const d = startOfDay(e.startDate);
    seen.set(toISODate(d), d);
  }
  const days = [...seen.values()].sort((a, b) => a.getTime() - b.getTime());
  return {
    days,
    minDate: days[0] ?? null,
    maxDate: days[days.length - 1] ?? null,
  };
}

/** Events that fall on `day` (by their start), sorted by start time. */
export function eventsForDay(events: NormalizedEvent[], day: Date): NormalizedEvent[] {
  return events
    .filter((e) => isSameDay(e.startDate, day))
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
}

/**
 * The day columns for a view, anchored on `anchor`. The multi-day views show
 * their full structural window — so Week and 5-day stay visually distinct
 * even when the data only spans weekdays; empty days render as empty columns.
 * Bounding to the data range is the job of navigation (`stepWeek`) and the
 * anchor clamp, not of trimming columns within the chosen week.
 *   - day:     [anchor]
 *   - fiveDay: Mon–Fri of anchor's week
 *   - week:    Mon–Sun of anchor's week
 */
export function getViewDays(view: CalendarView, anchor: Date, _data?: DataDays): Date[] {
  if (view === 'day') return [startOfDay(anchor)];

  const span = view === 'fiveDay' ? 5 : 7;
  const weekStart = startOfWeek(anchor);
  const all: Date[] = [];
  for (let i = 0; i < span; i++) all.push(addDays(weekStart, i));
  return all;
}

/** True when the view window anchored at `anchor` contains at least one day
 *  that has events. Used to detect an empty initial window so the renderer can
 *  auto-snap onto the nearest week that actually has data. */
export function viewHasData(view: CalendarView, anchor: Date, data: DataDays): boolean {
  if (data.days.length === 0) return false;
  const days = getViewDays(view, anchor, data);
  const lo = days[0].getTime();
  const hi = days[days.length - 1].getTime();
  return data.days.some((d) => d.getTime() >= lo && d.getTime() <= hi);
}

/** Previous data day strictly before `anchor`, or null at the lower bound. */
export function prevDataDay(data: DataDays, anchor: Date): Date | null {
  const t = startOfDay(anchor).getTime();
  for (let i = data.days.length - 1; i >= 0; i--) {
    if (data.days[i].getTime() < t) return data.days[i];
  }
  return null;
}

/** Next data day strictly after `anchor`, or null at the upper bound. */
export function nextDataDay(data: DataDays, anchor: Date): Date | null {
  const t = startOfDay(anchor).getTime();
  for (const d of data.days) if (d.getTime() > t) return d;
  return null;
}

/**
 * Step a multi-day view by one week, returning the new anchor, or null when
 * the resulting week would contain no data (so nav can disable at the bounds).
 */
export function stepWeek(view: CalendarView, anchor: Date, data: DataDays, dir: 1 | -1): Date | null {
  if (!data.minDate || !data.maxDate) return null;
  const next = addDays(anchor, 7 * dir);
  const days = getViewDays(view, next, data);
  const min = data.minDate.getTime();
  const max = data.maxDate.getTime();
  const hasData = days.some((d) => d.getTime() >= min && d.getTime() <= max);
  return hasData ? next : null;
}

// ─── Timeline layout (full density) ─────────────────────────────────────────

export interface TimeBounds {
  startHour: number;
  endHour: number;
}

/** Hour bounds for the full-view timeline. Uses explicit props when given,
 *  else derives from the events on the visible days (floor start / ceil end),
 *  falling back to 8–18 when there are no timed events. */
export function getTimeBounds(
  visibleEvents: NormalizedEvent[],
  dayStart?: number,
  dayEnd?: number,
): TimeBounds {
  const timed = visibleEvents.filter((e) => !e.allDay);
  let start = dayStart;
  let end = dayEnd;
  if (start == null || end == null) {
    if (timed.length === 0) {
      start ??= 8;
      end ??= 18;
    } else {
      let lo = 24;
      let hi = 0;
      for (const e of timed) {
        lo = Math.min(lo, e.startDate.getHours());
        const endH = e.endDate.getHours() + (e.endDate.getMinutes() > 0 ? 1 : 0);
        hi = Math.max(hi, endH);
      }
      start ??= Math.max(0, Math.floor(lo));
      end ??= Math.min(24, Math.ceil(hi));
    }
  }
  if (end <= start) end = Math.min(24, start + 1);
  return { startHour: start, endHour: end };
}

export interface PositionedEvent {
  event: NormalizedEvent;
  /** Percent offsets within the day column. */
  top: number;
  height: number;
  /** Column index and total columns for side-by-side overlap layout. */
  col: number;
  colCount: number;
}

function minutesInto(d: Date, startHour: number): number {
  return d.getHours() * 60 + d.getMinutes() - startHour * 60;
}

/**
 * Position the timed events of a single day onto the [startHour, endHour]
 * track, resolving overlaps into side-by-side columns. Events are clustered
 * into connected groups of mutual overlap; within a cluster each event takes
 * the first free column and the cluster's max concurrency sets colCount.
 */
export function layoutDay(dayEvents: NormalizedEvent[], bounds: TimeBounds): PositionedEvent[] {
  const totalMin = (bounds.endHour - bounds.startHour) * 60;
  const timed = dayEvents
    .filter((e) => !e.allDay)
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

  const result: PositionedEvent[] = [];
  let i = 0;
  while (i < timed.length) {
    // Build a cluster: events that transitively overlap.
    const cluster: NormalizedEvent[] = [timed[i]];
    let clusterEnd = timed[i].endDate.getTime();
    let j = i + 1;
    while (j < timed.length && timed[j].startDate.getTime() < clusterEnd) {
      cluster.push(timed[j]);
      clusterEnd = Math.max(clusterEnd, timed[j].endDate.getTime());
      j++;
    }

    // Greedy column assignment within the cluster.
    const colEnds: number[] = []; // end-time per column
    const assigned = cluster.map((e) => {
      let c = colEnds.findIndex((end) => end <= e.startDate.getTime());
      if (c === -1) {
        c = colEnds.length;
        colEnds.push(e.endDate.getTime());
      } else {
        colEnds[c] = e.endDate.getTime();
      }
      return c;
    });
    const colCount = colEnds.length;

    cluster.forEach((e, k) => {
      const top = (minutesInto(e.startDate, bounds.startHour) / totalMin) * 100;
      const durMin = (e.endDate.getTime() - e.startDate.getTime()) / 60_000;
      const height = (durMin / totalMin) * 100;
      result.push({
        event: e,
        top: Math.max(0, Math.min(100, top)),
        height: Math.max(1.5, Math.min(100 - Math.max(0, top), height)),
        col: assigned[k],
        colCount,
      });
    });
    i = j;
  }
  return result;
}

/** Hour rows (integers) spanning the bounds, for gridlines + the time gutter. */
export function hourRows(bounds: TimeBounds): number[] {
  const rows: number[] = [];
  for (let h = bounds.startHour; h <= bounds.endHour; h++) rows.push(h);
  return rows;
}

/**
 * Vertical offset (percent) of the current time within the bounds, or null
 * when `now` falls outside the visible hour track. Day-independent — the
 * caller decides whether today is among the visible days.
 */
export function timeOffsetPct(bounds: TimeBounds, now: Date): number | null {
  const totalMin = (bounds.endHour - bounds.startHour) * 60;
  const mins = minutesInto(now, bounds.startHour);
  if (mins < 0 || mins > totalMin) return null;
  return (mins / totalMin) * 100;
}

// ─── Formatting ─────────────────────────────────────────────────────────────

/** hour12: true → 12h, false → 24h, undefined → locale default. */
function timeOpts(hour12?: boolean): Intl.DateTimeFormatOptions {
  return hour12 === false
    ? { hour: '2-digit', minute: '2-digit', hour12: false }
    : { hour: 'numeric', minute: '2-digit', ...(hour12 === true ? { hour12: true } : {}) };
}

// Gutter hour labels mirror Google Calendar: 24h shows "10:00" / "13:00",
// 12h shows "10 AM" / "1 AM".
function hourOpts(hour12?: boolean): Intl.DateTimeFormatOptions {
  return hour12 === false
    ? { hour: '2-digit', minute: '2-digit', hour12: false }
    : { hour: 'numeric', ...(hour12 === true ? { hour12: true } : {}) };
}

export function formatTime(d: Date, hour12?: boolean): string {
  return d.toLocaleTimeString([], timeOpts(hour12)).replace(/\s/g, ' ');
}

export function formatHour(h: number, hour12?: boolean): string {
  const d = new Date(2000, 0, 1, ((h % 24) + 24) % 24);
  return d.toLocaleTimeString([], hourOpts(hour12));
}

/** The wall-clock hour for the SAME instant as a primary-local gridline,
 *  rendered in another IANA timezone (for the multi-timezone gutter). */
export function formatHourInZone(
  day: Date,
  hour: number,
  tz: string,
  hour12?: boolean,
): string {
  const instant = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0, 0);
  try {
    return new Intl.DateTimeFormat([], { timeZone: tz, ...hourOpts(hour12) }).format(instant);
  } catch {
    return '';
  }
}

export function formatTimeRange(e: NormalizedEvent, hour12?: boolean): string {
  if (e.allDay) return 'All day';
  return `${formatTime(e.startDate, hour12)} – ${formatTime(e.endDate, hour12)}`;
}

/** Short timezone abbreviation (e.g. "GMT+1", "EST") for a given day. */
export function tzAbbrev(day: Date, tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat([], {
      timeZone: tz,
      timeZoneName: 'short',
      hour: 'numeric',
    }).formatToParts(new Date(day.getFullYear(), day.getMonth(), day.getDate(), 12));
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? tz;
  } catch {
    return tz;
  }
}

/** A gutter column: the primary (local, no `tz`) plus up to two extra zones. */
export interface GutterColumnSpec {
  tz?: string;
  headerLabel: string;
}

/** Build the gutter columns — extra timezones to the left, primary (local)
 *  nearest the grid on the right. Capped at two extra zones. */
export function buildGutterColumns(day: Date, timezones?: TimezoneSpec[]): GutterColumnSpec[] {
  const extra = (timezones ?? [])
    .slice(0, 2)
    .map((z) => ({ tz: z.tz, headerLabel: z.label || tzAbbrev(day, z.tz) || z.tz }));
  // Only label the primary column when there are other columns to tell apart.
  return [...extra, { headerLabel: extra.length > 0 ? 'Local' : '' }];
}

export function formatDayLong(d: Date): string {
  return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
}

export function formatWeekday(d: Date): string {
  return d.toLocaleDateString([], { weekday: 'short' });
}

export function formatDayNum(d: Date): string {
  return String(d.getDate());
}

export function formatRangeLabel(days: Date[]): string {
  if (days.length === 0) return '';
  const first = days[0];
  const last = days[days.length - 1];
  // Build manually — partial Intl field combos (e.g. day + year only) format
  // unpredictably across engines, so we compose month/day/year ourselves.
  const mon = (d: Date) => d.toLocaleDateString([], { month: 'short' });
  if (isSameDay(first, last)) {
    return `${mon(first)} ${first.getDate()}, ${first.getFullYear()}`;
  }
  const sameYear = first.getFullYear() === last.getFullYear();
  const sameMonth = sameYear && first.getMonth() === last.getMonth();
  if (sameMonth) {
    return `${mon(first)} ${first.getDate()} – ${last.getDate()}, ${first.getFullYear()}`;
  }
  if (sameYear) {
    return `${mon(first)} ${first.getDate()} – ${mon(last)} ${last.getDate()}, ${first.getFullYear()}`;
  }
  return `${mon(first)} ${first.getDate()}, ${first.getFullYear()} – ${mon(last)} ${last.getDate()}, ${last.getFullYear()}`;
}

// ─── Status accent mapping ──────────────────────────────────────────────────

export interface StatusAccent {
  /** Left-border color class. */
  border: string;
  /** Small status dot background. */
  dot: string;
  /** Status label text color. */
  text: string;
  /** Display label (Title Case). */
  label: string;
}

// Full literal class strings per token group — Tailwind only generates
// classes it can see verbatim in source, so these must NOT be assembled at
// runtime from fragments.
const TOKEN_CLASSES = {
  neutral: { border: 'border-l-status-neutral-border', dot: 'bg-status-neutral-fg', text: 'text-status-neutral-fg' },
  warning: { border: 'border-l-status-warning-border', dot: 'bg-status-warning-fg', text: 'text-status-warning-fg' },
  success: { border: 'border-l-status-success-border', dot: 'bg-status-success-fg', text: 'text-status-success-fg' },
  info: { border: 'border-l-status-info-border', dot: 'bg-status-info-fg', text: 'text-status-info-fg' },
  error: { border: 'border-l-status-error-border', dot: 'bg-status-error-fg', text: 'text-status-error-fg' },
} as const;

const STATUS_TOKENS: Record<string, { token: keyof typeof TOKEN_CLASSES; label: string }> = {
  draft: { token: 'neutral', label: 'Draft' },
  pending: { token: 'warning', label: 'Pending' },
  booked: { token: 'success', label: 'Booked' },
  done: { token: 'info', label: 'Done' },
  cancelled: { token: 'error', label: 'Cancelled' },
  canceled: { token: 'error', label: 'Cancelled' },
};

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function statusAccent(status: string | undefined): StatusAccent | null {
  if (!status) return null;
  const mapped = STATUS_TOKENS[status.toLowerCase()];
  const classes = TOKEN_CLASSES[mapped?.token ?? 'neutral'];
  return {
    border: classes.border,
    dot: classes.dot,
    text: classes.text,
    label: mapped?.label ?? titleCase(status),
  };
}

// ─── Video-conference link detection ────────────────────────────────────────

export type ConferenceProvider = 'meet' | 'zoom' | 'teams' | 'other';

export interface ConferenceLink {
  provider: ConferenceProvider;
  url: string;
  /** "Join with Google Meet" / "Join Zoom Meeting" / … */
  label: string;
  /** Where the link was found, so callers can avoid showing it twice. */
  source: 'conferenceUrl' | 'location' | 'description';
}

const CONFERENCE_PATTERNS: { provider: ConferenceProvider; label: string; re: RegExp }[] = [
  { provider: 'meet', label: 'Join with Google Meet', re: /https?:\/\/meet\.google\.com\/[^\s<>"')\]]+/i },
  { provider: 'zoom', label: 'Join Zoom Meeting', re: /https?:\/\/(?:[\w-]+\.)?zoom\.us\/[^\s<>"')\]]+/i },
  { provider: 'teams', label: 'Join Microsoft Teams', re: /https?:\/\/teams\.(?:microsoft|live)\.com\/[^\s<>"')\]]+/i },
];

/**
 * Detect a known video-conference join link, preferring the event's explicit
 * `conferenceUrl`, then its location, then its description. Returns the first
 * match (with the recognized provider's label) or, for an explicit
 * `conferenceUrl` on an unknown host, a generic "Join meeting".
 */
export function detectConference(event: {
  conferenceUrl?: string;
  location?: string;
  description?: string;
}): ConferenceLink | null {
  const sources: [ConferenceLink['source'], string | undefined][] = [
    ['conferenceUrl', event.conferenceUrl],
    ['location', event.location],
    ['description', event.description],
  ];
  for (const [source, text] of sources) {
    if (!text) continue;
    for (const { provider, label, re } of CONFERENCE_PATTERNS) {
      const m = re.exec(text);
      if (m) return { provider, label, url: m[0], source };
    }
    // An explicit conferenceUrl on an unrecognized host still gets a join row.
    if (source === 'conferenceUrl' && /^https?:\/\/\S+$/i.test(text.trim())) {
      return { provider: 'other', label: 'Join meeting', url: text.trim(), source };
    }
  }
  return null;
}

/**
 * Strip an HTML description down to a single line of plain text. Calendar
 * descriptions (e.g. from Google Calendar) are frequently HTML; the compact
 * event card shows only a two-line preview where rendered block markup (lists,
 * paragraphs) would not fit, so we flatten tags to spaces and decode the common
 * entities. Plain-text descriptions pass through with whitespace collapsed.
 * The detail popover renders the full HTML via `MarkdownContent` instead.
 */
export function htmlToPlainText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|li|ol|ul|div|h[1-6]|tr)>/gi, ' ')
    // Outlook inline-image refs: [https://…png] — drop the bracketed URL.
    .replace(/\[<?https?:\/\/[^\]\s<>]+>?\]/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

/** True for descriptions carrying real HTML markup (e.g. Google Calendar),
 *  as opposed to plain text / Outlook's `LinkText<url>` convention. Matches a
 *  known element tag so `<mailto:…>` / `<https://…>` autolinks don't trip it. */
export function looksLikeHtml(s: string): boolean {
  return /<\/?(?:br|p|b|i|u|a|ol|ul|li|div|span|strong|em|h[1-6]|table|tr|td|blockquote|pre|code)(?:\s|>|\/)/i.test(
    s,
  );
}

/** True when plain text already reads as intentional markdown (headings,
 *  lists, setext rules, or explicit links) — so we don't force hard line
 *  breaks onto structured content. */
function looksLikeMarkdown(s: string): boolean {
  return (
    /^#{1,6}\s/m.test(s) ||
    /^\s*[-*]\s+\S/m.test(s) ||
    /^\s*\d+\.\s+\S/m.test(s) ||
    /\n[=-]{3,}\s*\n/.test(s) ||
    /\[[^\]]+\]\([^)]+\)/.test(s)
  );
}

/**
 * Normalize a calendar event description into markdown for `MarkdownContent`.
 *
 *  - HTML descriptions (Google Calendar) pass through untouched — rehype-raw
 *    renders them.
 *  - Outlook/Exchange plain-text bodies use a `LinkText<url>` convention (and
 *    `[imageUrl]<url>` for inline images) that otherwise renders as a raw URL.
 *    We rewrite those to real markdown so the visible label — e.g. the person
 *    before a `<mailto:>` or the phrase before a `<https://…>` — becomes the
 *    link, and inline-image refs render as images.
 *  - Plain text that isn't already markdown keeps its single line breaks
 *    (as markdown hard breaks) instead of collapsing into run-on paragraphs.
 */
export function normalizeDescriptionMarkup(text: string): string {
  if (looksLikeHtml(text)) return text;

  const wasMarkdown = looksLikeMarkdown(text);
  let s = text;

  // [imageUrl]<linkUrl> → inline image linking out (Outlook logos / branding).
  // Empty alt (decorative). The renderer hides the external-link icon for
  // image-only links so the image isn't trailed by a stray glyph.
  s = s.replace(
    /\[<?(https?:\/\/[^\]\s<>]+)>?\]<(https?:\/\/[^>\s]+)>/gi,
    (_m, img: string, link: string) => `[![](${img})](${link})`,
  );

  // mailto:/tel: angle links → the preceding token is the label (the person's
  // name before <mailto:>, the dialed number before <tel:>).
  s = s.replace(
    /(\S+?)\s*<((?:mailto|tel):[^>\s]+)>/gi,
    (_m, label: string, url: string) => `[${label}](${url})`,
  );

  // http(s) angle links → the preceding text on the line is the label
  // (e.g. "Join Zoom Meeting<https://…>"); fall back to a bare autolink.
  s = s.replace(
    /([^\n<>\]]+?)\s*<(https?:\/\/[^>\s]+)>/gi,
    (_m, label: string, url: string) => {
      const trimmed = label.trim();
      return trimmed ? `[${trimmed}](${url})` : `<${url}>`;
    },
  );

  if (!wasMarkdown) {
    // Preserve single line breaks as markdown hard breaks; leave blank-line
    // paragraph separators alone.
    s = s.replace(/([^\n])\n(?!\n)/g, '$1  \n');
  }
  return s;
}

/**
 * Format a participant as an RFC-5322 mailbox: `Display Name <email>`. The
 * display name is wrapped in quotes (with `"`/`\` escaped) when it contains a
 * `special` character — e.g. the period in `"Cihan T. Cetin" <…>` — so the
 * result pastes cleanly into a mail client's To: field.
 */
export function formatMailbox(name: string, email: string): string {
  const trimmed = name.trim();
  if (!trimmed) return email;
  if (/[()<>[\]:;@\\,."]/.test(trimmed)) {
    return `"${trimmed.replace(/([\\"])/g, '\\$1')}" <${email}>`;
  }
  return `${trimmed} <${email}>`;
}

// Re-exported for the renderer's prop typing convenience.
export type { CalendarDensity, CalendarView };
